import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { reserveOrCheckBuffer } from './reserveOrCheckBuffer';

import { clearBuffer, setBufferReadyToApply } from '../actions/buffer';
import { setBufferLocationId } from '../reducers/buffer/locationId';
import { setBufferSaleType } from '../reducers/buffer/saleType';
import { $getLocationId } from '../selectors/getLocationId';
import {
  getEnableDynamicPaymentGatewayConfig,
  getEnableMultipleDeliveryEstimate,
  getOrderingProvider,
} from '../selectors/config';
import { DELIVERY_SALE_TYPES, SALE_TYPE } from '../constants/saleType';
import { FAILURE_REASON } from '../constants';
import { applyLocationEstimate } from './applyLocationEstimate';
import {
  getBufferDeliveryTime,
  getLocation,
  getLocationDeliveryEstimates,
  getMember,
  getPickupTime,
} from '../selectors';
import { fetchOffers } from './fetchOffers';
import { applyBuffer } from './applyBuffer';
import { fetchMenu } from './fetchMenu';
import { fetchPaymentGatewayConfig } from './fetchPaymentGatewayConfig';
import { fetchOrderingProviderDetails } from './fetchOrderingProviderDetails';
import { fetchDeliveryEstimate } from './fetchDeliveryEstimate';
import {
  fetchPaymentGatewayTokens,
  setBypassDeliveryDetails,
} from '../operations';
import { ORDERING_PROVIDER } from '../constants/orderingProvider';
import Logger from '../utils/Logger';
import moment from 'moment';
import { IDeliveryDropOffCoordinatesHttpDto } from '../dtos/interfaces';
import { isEmpty } from 'lodash';

export interface UpdateKeyOrderProperyDto {
  locationId?: string;
  saleType?: SALE_TYPE;
  autoApply?: boolean;
  desiredDeliveryTime?: string;
  deliveryAddress?: string;
  updateDeliveryEstimate?: boolean;
  confirmLocationDeliveryEstimate?: boolean;
  forceASAPDeliveryEstimate?: boolean;
  bypassDeliveryDetails?: boolean;
  isASAP?: boolean;
  readonly deliveryDropOffCoordinates?: IDeliveryDropOffCoordinatesHttpDto;
}
export const updateKeyOrderProperty = createAsyncThunk(
  '$updateKeyOrderProperty',
  async (
    data: UpdateKeyOrderProperyDto,
    { dispatch, getState, rejectWithValue },
  ) => {
    const {
      locationId,
      saleType,
      autoApply,
      desiredDeliveryTime,
      deliveryAddress,
      updateDeliveryEstimate,
      confirmLocationDeliveryEstimate,
      forceASAPDeliveryEstimate,
      bypassDeliveryDetails,
      isASAP,
      deliveryDropOffCoordinates
    } = data;

    let controlsBuffer;
    let reason = FAILURE_REASON.UNKNOWN;
    let userReason: string | undefined;
    let systemReason: string | undefined;
    let receivedMultipleEstimates = false;

    const orderingProvider = getOrderingProvider(
      getState() as EntireFrontendState,
    );
    const isDelivery = saleType && DELIVERY_SALE_TYPES.includes(saleType);

    try {
      const flowId = uuidv4();
      dispatch(reserveOrCheckBuffer({ flowId }));

      controlsBuffer = true;

      const initialBufferLocationId = $getLocationId(
        getState() as EntireFrontendState,
      );

      const enableMultipleDeliveryEstimateConfig =
        getEnableMultipleDeliveryEstimate(getState() as EntireFrontendState);

      const enableMultipleDeliveryEstimate = bypassDeliveryDetails
        ? false
        : enableMultipleDeliveryEstimateConfig;

      if (!isDelivery && locationId != null) {
        dispatch(setBypassDeliveryDetails(false));
      }

      const performDeliveryEstimate =
        !confirmLocationDeliveryEstimate &&
        (isDelivery || updateDeliveryEstimate) &&
        (!bypassDeliveryDetails || deliveryAddress);

      if (saleType != null) {
        const location = getLocation(getState() as EntireFrontendState);
        if (
          location &&
          !location.availableSaleTypesToday[saleType].isAvailable &&
          !isDelivery
        ) {
          throw new Error(
            `Saletype ${saleType} is not available now for location ${location.name}`,
          );
        }

        Logger.log('Setting buffer saletype : ' + saleType);
        dispatch(setBufferSaleType(saleType));
      }

      if (!performDeliveryEstimate && locationId != null) {
        dispatch(setBufferLocationId(locationId));
      }

      // performing delivery estimate
      if (performDeliveryEstimate) {
        if (locationId != null && !bypassDeliveryDetails) {
          throw new Error('cannot provide locationId with delivery saleType');
        }

        let fetchParams: any = { forceASAPDeliveryEstimate };

        if (updateDeliveryEstimate) {
          if (enableMultipleDeliveryEstimate) {
            fetchParams = {
              ...fetchParams,
              locationId: initialBufferLocationId,
            };
          }
        } else {
          fetchParams = {
            ...fetchParams,
            deliveryAddress,
            desiredDeliveryTime,
            multiple: enableMultipleDeliveryEstimate,
            locationId: bypassDeliveryDetails ? locationId : undefined,
            saleType: saleType,
          };
        }

        if (deliveryAddress) {
          const { multiple } = await dispatch(
            fetchDeliveryEstimate({
              ...fetchParams, 
              ...(!isEmpty(deliveryDropOffCoordinates) ? {deliveryDropOffCoordinates}: {})
            }),
          ).unwrap();

          console.log({ multiple });

          if (multiple) {
            receivedMultipleEstimates = true;
          }
        }
      }

      //confirm delivery estimate
      if (confirmLocationDeliveryEstimate) {
        if (locationId === null) {
          throw new Error('missing locationId');
        }

        const locationEstimates = getLocationDeliveryEstimates(
          getState() as EntireFrontendState,
        );
        const estimate = locationEstimates.find(
          locationEstimate => locationEstimate.locationId === locationId,
        );

        if (!estimate) {
          throw new Error('no matching estimate for locationId');
        }

        // Apply the location estimate
        dispatch(
          applyLocationEstimate({
            ...estimate,
            bestDeliveryTime: estimate.bestDeliveryTime,
            isASAP,
          }),
        );
      }

      //handle multiple estimates
      if (!receivedMultipleEstimates) {
        const bufferLocationId = $getLocationId(
          getState() as EntireFrontendState,
        );

        if (bufferLocationId == null) {
          return;
        }

        const bufferLocationIdChanged =
          bufferLocationId !== initialBufferLocationId;

        if (bufferLocationIdChanged || !updateDeliveryEstimate) {
          //fetch menu
          await dispatch(fetchMenu()).unwrap();
        }

        if (bufferLocationIdChanged) {
          const memberPresent = Boolean(
            getMember(getState() as EntireFrontendState),
          );

          if (memberPresent) {
            //fetch offers
            await dispatch(fetchOffers({ bufferMode: true }));
          }
        }

        if (!performDeliveryEstimate || !updateDeliveryEstimate) {
          const pickupDateTime = getPickupTime(
            getState() as EntireFrontendState,
          );

          // Delivery time is not set from buffer in fetchOrderingProviderDetails at this point
          const bufferDeliveryDateTime = getBufferDeliveryTime(
            getState() as EntireFrontendState,
          );

          let dateTime;
          if (isDelivery && bufferDeliveryDateTime !== 'ASAP') {
            dateTime = bufferDeliveryDateTime;
          } else {
            dateTime = pickupDateTime;
          }

          //fetch ordering provider details
          if (saleType) {
            await dispatch(
              fetchOrderingProviderDetails({
                bufferMode: true,
                date:
                  dateTime && dateTime !== 'ASAP'
                    ? moment(dateTime).format('l')
                    : undefined,
              }),
            ).unwrap();
          }
        }

        const enableDynamicPaymentGatewayConfig =
          getEnableDynamicPaymentGatewayConfig(
            getState() as EntireFrontendState,
          );

        if (enableDynamicPaymentGatewayConfig) {
          //fetch payment gateway config
          await dispatch(
            fetchPaymentGatewayConfig({
              bufferMode: true,
              locationId: bufferLocationId,
            }),
          ).unwrap();
        } else {
          if (orderingProvider !== ORDERING_PROVIDER.KIOSK) {
            await dispatch(fetchPaymentGatewayTokens({}));
          }
        }
      }

      dispatch(setBufferReadyToApply(!receivedMultipleEstimates));

      if (
        !receivedMultipleEstimates &&
        (autoApply || updateDeliveryEstimate || confirmLocationDeliveryEstimate)
      ) {
        await dispatch(applyBuffer()).unwrap();
      }
    } catch (e) {
      if (controlsBuffer) {
        dispatch(clearBuffer({}));
      }

      console.warn('Update key order property failed', e, {
        reason,
        userReason,
        systemReason,
      });
      return rejectWithValue({
        //@ts-ignore
        ...e,
        reason: (e as any).reason ? (e as any).reason : reason,
        userReason: (e as any).userReason ? (e as any).userReason : userReason,
        systemReason: (e as any).systemReason
          ? (e as any).systemReason
          : systemReason,
      });
    }
  },
);
